'use client'

import { PlusGrid, PlusGridItem, PlusGridRow } from '@/components/plus-grid'
import { Button } from './button'
import { Container } from './container'
import { Gradient } from './gradient'
import { Link } from './link'
import { Logo } from './logo'
import { Subheading } from './text'

function CallToAction() {
  return (
    <div className="relative pt-20 pb-16 text-center sm:py-24">
        <hgroup>
          <Subheading>Let&apos;s Connect</Subheading>
          <p className="mt-6 text-3xl font-medium tracking-tight text-gray-950 sm:text-5xl">
            Ready to transform your idea?
            <br />
            Let&apos;s build something amazing together.
          </p>
        </hgroup>
        <p className="mx-auto mt-6 max-w-xs text-sm/6 text-gray-500">
          Book a free consultation to discuss your project and explore how we can help.
        </p>
      <div className="mt-6">
        <Button 
          className="w-full sm:w-auto" 
          data-cal-namespace="15min"
          data-cal-link="mohammed-saket-d4virq/15min"
          data-cal-config='{"layout":"month_view"}'
          onClick={() => {
            if (typeof window !== 'undefined' && window.gtag) {
              window.gtag('event', 'book_button_clicked', {
                event_category: 'engagement',
                event_label: 'footer',
                value: 1,
              })
            }
          }}
        >
          Get In Touch
        </Button>
      </div>
    </div>
  )
}

function SocialIconGitHub(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
    </svg>
  )
}

function SocialIconLinkedIn(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 16 16" fill="currentColor" {...props}>
      <path d="M14.82 0H1.18A1.169 1.169 0 000 1.154v13.694A1.168 1.168 0 001.18 16h13.64A1.17 1.17 0 0016 14.845V1.15A1.171 1.171 0 0014.82 0zM4.744 13.64H2.369V5.996h2.375v7.644zm-1.18-8.684a1.377 1.377 0 11.52-.106 1.377 1.377 0 01-.527.103l.007.003zm10.075 8.683h-2.375V9.921c0-.885-.015-2.025-1.234-2.025-1.218 0-1.425.966-1.425 1.968v3.775H6.233V5.997H8.51v1.05h.032c.317-.601 1.09-1.235 2.246-1.235 2.405-.005 2.851 1.578 2.851 3.63v4.197z" />
    </svg>
  )
}

function SocialLinks() {
  return (
    <>
      <Link
        href="https://github.com/mohammedsaket"
        target="_blank"
        aria-label="Visit my GitHub"
        className="text-gray-950 data-hover:text-gray-950/75"
        onClick={() => {
          if (typeof window !== 'undefined' && window.gtag) {
            window.gtag('event', 'social_link_clicked', {
              event_category: 'engagement',
              event_label: 'github',
              value: 1,
            })
          }
        }}
      >
        <SocialIconGitHub className="size-5" />
      </Link>
      <Link
        href="https://www.linkedin.com/in/mohammed-saket/"
        target="_blank"
        aria-label="Visit my LinkedIn"
        className="text-gray-950 data-hover:text-gray-950/75"
        onClick={() => {
          if (typeof window !== 'undefined' && window.gtag) {
            window.gtag('event', 'social_link_clicked', {
              event_category: 'engagement',
              event_label: 'linkedin',
              value: 1,
            })
          }
        }}
      >
        <SocialIconLinkedIn className="size-5" />
      </Link>
    </>
  )
}

function Copyright() {
  return (
    <div className="text-sm/6 text-gray-950">
      &copy; {new Date().getFullYear()} Saisha Consultancy. All rights reserved.
    </div>
  )
}

export function Footer() {
  return (
    <footer>
      <Gradient className="relative">
        <div className="absolute inset-2 rounded-4xl bg-white/80" />
        <Container>
          <CallToAction />
          <PlusGrid className="pb-16">
            <PlusGridRow>
              <div className="grid grid-cols-1 gap-y-10 pb-6 lg:grid-cols-3 lg:gap-8">
                <div className="flex">
                  <PlusGridItem>
                    <Logo className="h-9" />
                  </PlusGridItem>
                </div>
                <div className="lg:col-span-2 lg:pt-6">
                  <div className="text-center lg:text-left">
                    <p className="text-sm text-gray-600">
                      Your partner in building scalable MVPs & SaaS products.
                    </p>
                  </div>
                </div>
              </div>
            </PlusGridRow>
            <PlusGridRow className="flex justify-between">
              <div>
                <PlusGridItem className="py-3">
                  <Copyright />
                </PlusGridItem>
              </div>
              <div className="flex">
                <PlusGridItem className="flex items-center gap-8 py-3">
                  <SocialLinks />
                </PlusGridItem>
              </div>
            </PlusGridRow>
          </PlusGrid>
        </Container>
      </Gradient>
    </footer>
  )
}

